/**
 * German Translations
 *
 * Uses official game terminology from the Waterloo rules by Josef Emanuel Schmidt.
 * Key terms: General, Verteidiger, Deck, Handkarten, stechen/ausgestochen, verdeckte Karte
 */
export default {
  // Game status
  'game.yourTurn': 'Du bist dran',
  'game.waitingFor': 'Warte auf {playerName}',
  'game.connecting': 'Verbinde...',
  'game.reconnecting': 'Verbinde erneut...',
  'game.winner': 'Gewinner: {playerName}',
  'game.wins': '{playerName} gewinnt!',
  'game.draw': 'Unentschieden!',
  'game.gameEnded': 'Spiel endete unentschieden!',

  // Stats labels
  'stats.phase': 'Phase',
  'stats.central': 'Zentral',
  'stats.stack': 'Deck',
  'stats.hand': 'Hand',
  'stats.unknown': 'unbekannt',

  // Phase messages
  'phase.clickToStart': 'Klicke auf das Kartendeck um das Spiel zu starten',
  'phase.waitingForStart': 'Warte darauf, dass {playerName} das Spiel startet...',
  'phase.distributing': 'Verteile Karten an die Spieler-Decks...',
  'phase.dealing': 'Gebe Karten an {playerName}...',
  'phase.redistributing': 'Verteile Karten vom besiegten {playerName}...',
  'phase.gameEnding': 'Spiel endet... Bewege verbleibende Karten...',

  // Action hints
  'hint.bonusExposed': 'General aufgedeckt! Klicke auf Deck für Bonuskarte.',
  'hint.priorityTurn': 'Prioritätszug!',
  'hint.draw3Cards': 'Klicke auf Deck um 3 Karten zu ziehen.',
  'hint.draw2Cards': 'Klicke auf Deck um 2 Karten zu ziehen.',
  'hint.draw1Card': 'Klicke auf Deck um 1 Karte zu ziehen.',
  'hint.unstash': 'Klicke auf verdeckte Karte zum Aufdecken.',
  'hint.selectCard': 'Klicke auf eine Handkarte um sie auszuwählen.',
  'hint.cardSelected': 'Karte ausgewählt. Klicke auf ein markiertes Ziel, oder erneut auf die Karte zum Abwählen.',
  'hint.waitingForPlayer': 'Warte auf {playerName}...',

  // Buttons
  'button.endTurn': 'Runde beenden',
  'button.continue': 'Weiter',
  'button.create': 'Erstellen',
  'button.creating': 'Erstelle...',
  'button.join': 'Beitreten',
  'button.joining': 'Trete bei...',
  'button.back': 'Zurück',
  'button.save': 'Speichern',
  'button.copyCode': 'Code kopieren',
  'button.copyLink': 'Link kopieren',
  'button.copied': 'Kopiert!',
  'button.changeName': 'Name ändern',
  'button.ok': 'OK',

  // Lobby
  'lobby.title': 'Waterloo',
  'lobby.enterName': 'Gib deinen Namen ein um fortzufahren',
  'lobby.welcome': 'Willkommen, {name}!',
  'lobby.createMatch': 'Partie erstellen',
  'lobby.joinMatch': 'Partie beitreten',
  'lobby.numPlayers': 'Anzahl Spieler',
  'lobby.players2': '2 Spieler',
  'lobby.players3': '3 Spieler',
  'lobby.players4': '4 Spieler',
  'lobby.matchCode': 'Spielcode',
  'lobby.yourName': 'Dein Name',
  'lobby.waitingRoom': 'Warteraum',
  'lobby.matchCodeLabel': 'Spielcode',
  'lobby.playersCount': 'Spieler ({joined}/{total})',
  'lobby.playerSlot': 'S{number}',
  'lobby.waiting': 'Wartet...',
  'lobby.you': '(Du)',
  'lobby.autoStart': 'Spiel startet automatisch wenn alle Spieler beigetreten sind',

  // Errors
  'error.enterMatchCode': 'Bitte Spielcode eingeben',
  'error.matchFull': 'Partie ist voll',
  'error.createFailed': 'Partie erstellen fehlgeschlagen: {message}',
  'error.joinFailed': 'Beitreten fehlgeschlagen: {message}',
  'error.rematchFailed': 'Revanche beitreten fehlgeschlagen: {message}',

  // Rematch modal
  'rematch.gameOver': 'Spiel beendet',
  'rematch.joiningRematch': 'Trete Revanche bei...',
  'rematch.connecting': 'Verbinde mit neuer Partie',
  'rematch.askForRematch': 'Revanche anfordern',
  'rematch.notEnoughPlayers': 'Nicht genügend Spieler für Revanche',
  'rematch.status': 'Revanche-Status',
  'rematch.requested': 'angefordert',
  'rematch.ready': 'Bereit',
  'rematch.pending': 'Wartet...',
  'rematch.declined': 'Abgelehnt',
  'rematch.startMatch': '{count}-Spieler Partie starten',
  'rematch.starting': 'Starte...',
  'rematch.cancelRematch': 'Revanche abbrechen',
  'rematch.wantsRematch': '{playerName} möchte eine Revanche!',
  'rematch.accept': 'Revanche annehmen',
  'rematch.decline': 'Ablehnen',
  'rematch.orStartDifferent': 'oder eine andere Partie starten',

  // Rules modal
  'rules.title': 'Spielregeln',
  'rules.close': 'Schließen',
  'rules.buttonTitle': 'Spielregeln anzeigen',
  'rules.intro': 'Benötigt wird ein bayerisches Spielkartenset. Für zwei bis vier Spieler.\n\nZu Beginn der Partie werden alle Karten an alle Spieler gleichmäßig verteilt. Dabei dürfen die Karten NICHT eingesehen werden, sondern werden als "Deck" senkrecht vor den Spieler gelegt. Jeder Spieler zieht anschließend fünf Karten von seinem Deck. Eine der Karten wird als "General" senkrecht offen neben das Deck gelegt, zwei weitere als "Verteidiger" waagrecht offen vor den General. Die verbleibenden Karten werden nicht gezeigt, sondern als Handkarten in der ersten Runde ausgespielt. Ziel des Spiels ist es, der letzte Spieler zu sein, dessen General überlebt.',
  'rules.cardValuesTitle': 'Kartenwerte',
  'rules.cardValues': 'Sechs=1, Sieben=2, Acht=3, Neun=4, Zehn=5, Unter=6, Ober=7, König=8, Ass=9',
  'rules.turnStart': 'Zu Beginn jeder Runde zieht der Spieler zwei Handkarten von seinem Deck. Alle Handkarten müssen ausgespielt werden, bevor der Spieler seine Runde beenden darf.',
  'rules.actionsTitle': 'Mögliche Aktionen',
  'rules.action1': 'Eine gegnerische Karte angreifen: Jede Handkarte darf auf einen beliebigen Verteidiger eines beliebigen gegnerischen Spielers gelegt werden, um diesen anzugreifen; es dürfen auch beide Handkarten denselben Verteidiger angreifen. Die angreifenden Karten müssen so gelegt werden, dass die Werte aller angreifenden Karten sowie des Verteidigers noch erkennbar sind. Ist der zusammengerechnete Wert aller angreifenden Karten gleich oder niedriger als der des Verteidigers, bleiben die angreifenden Karten liegen. Ist der Wert der Angreifer höher, wird der Verteidiger "gestochen" und er (sowie alle angreifenden Karten) gehen unten in das Deck des aktuell angreifenden Spielers ein.',
  'rules.action2': 'Die Karte als Verteidiger legen: Hat der aktive Spieler weniger als zwei Verteidiger, darf er die fehlenden Verteidiger mit seinen Handkarten ersetzen. Es dürfen maximal zwei Verteidiger gleichzeitig vor dem General liegen.',
  'rules.action3': 'Eine Handkarte verdeckt beiseitelegen, sodass man diese in einer späteren Runde wieder aufdecken und verwenden kann. Es darf maximal EINE Karte verdeckt sein. Wenn man die verdeckte Karte verwenden möchte, muss diese ZU BEGINN DER RUNDE aufgenommen werden. Anschließend wird eine Karte weniger vom Stapel gezogen, sodass die Anzahl an Handkarten gleich bleibt.',
  'rules.action4': 'Wenn es sich bei der gezogenen Karte um ein Ass handelt, darf man alternativ zu den anderen Optionen einen eigenen Verteidiger oder General durch das Ass ersetzen. Dabei gehen die ersetzte Karte und dessen Angreifer unter den Stapel des Besitzers des Asses ein.',
  'rules.generalAttack': 'Hat ein gegnerischer Spieler keine Verteidiger mehr (d.h. seine Verteidigung wurde "ausgestochen"), greift der aktive Spieler mit seinen verbleibenden Handkarten den General des gegnerischen Spielers an. Nutzt der aktive Spieler seine letzte Handkarte, um den letzten Verteidiger eines gegnerischen Spielers zu stechen, wird die oberste Karte vom Deck des angreifenden Spielers gezogen, welche daraufhin den General des ausgestochenen Spielers angreift. Wenn ein Spieler ausgestochen wird, ohne seinen General zu verlieren, so ist er unmittelbar in der nächsten Runde dran. In diesem Fall darf er zu Beginn seiner Runde eine zusätzliche Karte von seinem Deck ziehen.',
  'rules.losing': 'Ein Spieler verliert die Partie, wenn sein General gestochen wird oder er "verhungert": sein Deck hat zu Beginn seiner Runde weniger Karten, als er ziehen müsste (nach Punkt 3 verdeckte Karten werden berücksichtigt). Verliert ein Spieler, werden sein General, seine Verteidiger, alle angreifenden Karten und ggf. seine verdeckte Karte unter sein Deck gelegt; dieses wird anschließend gemischt und an die verbleibenden Spieler verteilt, welche die erhaltenen Karten unter ihr Deck legen. Jede Partie wird gespielt, bis nur noch ein Spieler im Spiel ist. Der letzte überlebende Spieler gewinnt die Partie.',
  'rules.tipTitle': 'Tipp',
  'rules.tip': 'Es ist sinnvoll, sein Deck zu "pflegen", indem bevorzugt starke Karten gestochen werden und schwache Handkarten nach Möglichkeit anderen Spielern überlassen werden. Asse sind mit Abstand die wertvollsten Karten, daher sollten diese nach Möglichkeit nicht an andere Spieler gelangen.',

  // Attributions
  'attributions.title': 'Danksagungen',
  'attributions.close': 'Schließen',
  'attributions.buttonTitle': 'Credits und Danksagungen anzeigen',
  'attributions.gameDesignTitle': 'Spieldesign',
  'attributions.gameDesignBy': 'Originalregeln von Josef Emanuel Schmidt',
  'attributions.developmentTitle': 'Entwicklung',
  'attributions.developedBy': 'Digitale Umsetzung von Jakob Schmidt',
  'attributions.builtWithTitle': 'Erstellt mit Hilfe von',
  'attributions.builtWith': 'boardgame.io - Mehrspieler-Spielframework & cards.js - Karten-Rendering-Bibliothek',
  'attributions.showSourceCode': 'Quellcode anzeigen',

  // Changelog
  'changelog.title': 'Versionsgeschichte',
  'changelog.close': 'Schließen',
  'changelog.buttonTitle': 'Versionsgeschichte anzeigen',

  // Mode switcher
  'mode.switchToDev': 'Zum Entwicklermodus wechseln',
  'mode.switchToOnline': 'Zum Online-Modus wechseln',
  'changelog.v0_15': 'Anpassungen für Docker-Deployment in Produktionsumgebung',
  'changelog.v0_14': 'Versions-Button mit Changelog-Modal hinzugefügt\nVersionsanzeige in die untere linke Schaltflächenleiste verschoben',
  'changelog.v0_13': 'Regeln-Button mit übersetzten Spielregeln hinzugefügt',
  'changelog.v0_12': 'Mehrsprachige Unterstützung hinzugefügt (Deutsch und Englisch)',
  'changelog.v0_11': '"König" wurde zu "General" umbenannt\nAngriffsanimation auf General-Angriffe erweitert\nSanfte Overlay-Einblendungsanimationen hinzugefügt\nVerbesserte visuelle Hervorhebung beim Aufdecken von Karten',
  'changelog.v0_10': 'Handkarten-Parkbereich für bessere Kartenverwaltung hinzugefügt\nAngriffsanimationen beim Stechen von Karten implementiert\nKonsistente Tischpositionen für alle Spieler korrigiert',
  'changelog.v0_9': 'Individuelle Spielerrotation für Mehrspieler-Tischanordnung hinzugefügt',
  'changelog.v0_8': 'Online-Mehrspieler mit Lobby-System implementiert\nRevanche-Funktionalität nach Spielende hinzugefügt\nKartenverteilung beim Ausscheiden von Spielern hinzugefügt\nKern-Spiellogik mit Kartenhervorhebung und -auswahl',
};
